import React, { useState } from 'react';
import { submitTicket, lookupTicket } from '../services/api';
import './Support.css';

const STATUS_COLORS = { open: 'badge-warning', in_progress: 'badge-info', resolved: 'badge-success', closed: 'badge-gray' };

export default function Support() {
  const [tab, setTab] = useState('submit');
  const [form, setForm] = useState({ name: '', email: '', orderNumber: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lookupNum, setLookupNum] = useState('');
  const [ticket, setTicket] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      const { data } = await submitTicket(form);
      setSubmitted(data);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    setLookupError('');
    setTicket(null);
    try {
      const { data } = await lookupTicket(lookupNum.trim());
      setTicket(data);
    } catch {
      setLookupError('Ticket not found. Please check your ticket number.');
    }
  };

  return (
    <div className="container" style={{ padding: '2rem 1.5rem', maxWidth: '700px' }}>
      <div className="page-header">
        <h1>Customer Support</h1>
        <p>We're here to help. Submit a ticket or check an existing one.</p>
      </div>

      <div className="support-tabs">
        <button className={`support-tab ${tab === 'submit' ? 'active' : ''}`} onClick={() => setTab('submit')}>
          📬 Submit a Ticket
        </button>
        <button className={`support-tab ${tab === 'lookup' ? 'active' : ''}`} onClick={() => setTab('lookup')}>
          🔍 Check Ticket Status
        </button>
      </div>

      {tab === 'submit' && (
        <div className="card">
          {submitted ? (
            <div className="submission-success">
              <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>📬</div>
              <h2>Ticket Submitted!</h2>
              <p>Your ticket number is:</p>
              <div className="ticket-number">{submitted.ticketNumber}</div>
              <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>
                {submitted.message}
              </p>
              <button className="btn btn-secondary" onClick={() => { setSubmitted(null); setForm({ name:'', email:'', orderNumber:'', subject:'', message:'' }); }}>
                Submit Another Ticket
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h2 style={{ marginBottom: '1.25rem', fontSize: '1.05rem' }}>Contact Us</h2>
              <div className="support-contact-note">
                Submit a ticket below and our support team will review it as soon as possible.
              </div>
              {formError && <div className="alert alert-error">{formError}</div>}

              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Your Name *</label>
                  <input className="form-control" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input className="form-control" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Order Number (optional)</label>
                <input className="form-control" placeholder="ORD-XXXXXXXX" value={form.orderNumber} onChange={e => setForm({...form, orderNumber: e.target.value})} />
              </div>

              <div className="form-group">
                <label className="form-label">Subject *</label>
                <select className="form-control" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} required>
                  <option value="">Select a topic...</option>
                  <option>Where is my order?</option>
                  <option>Item arrived damaged</option>
                  <option>Wrong item received</option>
                  <option>Return or refund request</option>
                  <option>Payment issue</option>
                  <option>Other</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Message *</label>
                <textarea
                  className="form-control"
                  rows={5}
                  placeholder="Please describe your issue in detail..."
                  value={form.message}
                  onChange={e => setForm({...form, message: e.target.value})}
                  required
                />
              </div>

              <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={submitting}>
                {submitting ? 'Submitting...' : '📤 Submit Ticket'}
              </button>
            </form>
          )}
        </div>
      )}

      {tab === 'lookup' && (
        <div className="card">
          <h2 style={{ marginBottom: '1.25rem', fontSize: '1.05rem' }}>Check Ticket Status</h2>
          <form onSubmit={handleLookup} style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
            <input
              className="form-control"
              placeholder="Enter ticket number (TKT-XXXXXXXX)"
              value={lookupNum}
              onChange={e => setLookupNum(e.target.value)}
            />
            <button className="btn btn-primary" type="submit">Check</button>
          </form>

          {lookupError && <div className="alert alert-error">{lookupError}</div>}

          {ticket && (
            <div className="ticket-detail">
              <div className="ticket-detail-row">
                <span>Ticket #</span>
                <strong>{ticket.ticketNumber}</strong>
              </div>
              <div className="ticket-detail-row">
                <span>Status</span>
                <span className={`badge ${STATUS_COLORS[ticket.status] || 'badge-gray'}`}>{ticket.status}</span>
              </div>
              <div className="ticket-detail-row">
                <span>Subject</span>
                <span>{ticket.subject}</span>
              </div>
              <div className="ticket-message">
                <p className="ticket-label">Your Message:</p>
                <p>{ticket.message}</p>
              </div>
              {ticket.adminReply && (
                <div className="ticket-reply">
                  <p className="ticket-label">Our Response:</p>
                  <p>{ticket.adminReply}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
